/*******************************************************************************
 *
 * This file is part of the LGS1920/studio project.
 *
 * File: LayersColorsAdjustementPopup.jsx
 *
 * Author : LGS1920 Team
 * email: studio@lgs1920.fr
 *
 * Created on: 2026-05-10
 * Last modified: 2026-05-10
 *
 *
 * Copyright © 2026 LGS1920
 ******************************************************************************/

import { Range }                               from '@Components/Range'
import { BASE_ENTITY, DEFAULT_LAYERS_COLOR_SETTINGS, OVERLAY_ENTITY } from '@Core/constants'
import { ensureLayerColorSettings } from '@Utils/cesium/LayerColorSettingsUtils'
import { LayersUtils }                         from '@Utils/cesium/LayersUtils'
import { WaButton, WaCard, WaIcon, WaTooltip } from '@web.awesome.me/webawesome-pro/dist/react'
import { useEffect }                           from 'react'
import { useSnapshot }                         from 'valtio/index'

/**
 * Component to manage layer-specific visual settings like hue, saturation, etc.
 * Uses Valtio proxies for global state management.
 */
export const LayersColorsAdjustementPopup = (props) => {
    const $editor = lgs.editorSettingsProxy
    const editor = useSnapshot($editor)

    const $layers = lgs.settings.layers
    const layers = useSnapshot($layers)

    /**
     * Resets settings to factory defaults.
     */
    const resetToFactory = () => {
        const layerKey = layers[editor.layer.selectedType]
        $editor.layer.settingsChanged = false
        $layers.colorSettings[layerKey] = {...DEFAULT_LAYERS_COLOR_SETTINGS}
        LayersUtils.applySettings($layers.colorSettings[layerKey], editor.layer.selectedType)
    }

    /**
     * Reverts changes to the last known state stored in the application context.
     */
    const undoChanges = () => {
        const layerKey = layers[editor.layer.selectedType]
        $editor.layer.settingsChanged = false
        $layers.colorSettings[layerKey] = {...lgs.theDefaultColorSettings}
        LayersUtils.applySettings($layers.colorSettings[layerKey], editor.layer.selectedType)
    }

    /**
     * Closes the settings panel and resets modification flags.
     */
    const close = () => {
        $editor.openSettings = !$editor.openSettings
        $editor.settingsChanged = true
    }

    /**
     * Handles slider updates and propagates them to the underlying engine.
     */
    const changeHandler = (name, current) => {
        const layerKey = layers[editor.layer.selectedType]
        $editor.layer.settingsChanged = true
        $layers.colorSettings[layerKey][name] = current * 1
        LayersUtils.applySettings($layers.colorSettings[layerKey], editor.layer.selectedType)
    }

    useEffect(() => {
        if (![BASE_ENTITY, OVERLAY_ENTITY].includes(editor.layer.selectedType)) {
            return
        }
        ensureLayerColorSettings({layersProxy: $layers, selectedType: editor.layer.selectedType})
    }, [editor.layer.selectedType, editor.layer.settingsChanged, $layers])

    return (
        <>
            {editor.openSettings && props.visible() &&
                <WaCard id="layer-settings" key="layer-entities"
                        className="lgs--popup-in-drawer lgs-slide-down fix-margin"
                        appearance="outlined">
                    <WaButton appearance="plain"
                              slot="header-actions"
                              onClick={close}>
                        <WaIcon size="s" name="xmark" variant="regular"/>
                    </WaButton>

                    <h3 slot="header">
                        <WaIcon name="sliders" variant="regular"/> {'Color Adjustement'}
                    </h3>

                    <Range label="Hue"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].hue}
                           min={0} max={359} step={1} onChange={changeHandler}
                           name="hue"
                    />
                    <Range label="Saturation"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].saturation}
                           min={0} max={100} step={1} onChange={changeHandler}
                           name="saturation"
                    />
                    <Range label="Alpha"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].alpha}
                           min={0} max={3} step={0.05} onChange={changeHandler}
                           name="alpha"
                    />
                    <Range label="Gamma"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].gamma}
                           min={0} max={3} step={0.05} onChange={changeHandler}
                           name="gamma"
                    />
                    <Range label="Contrast"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].contrast}
                           min={0} max={3} step={0.05} onChange={changeHandler}
                           name="contrast"
                    />
                    <Range label="Brightness"
                           value={layers.colorSettings[layers[editor.layer.selectedType]].brightness}
                           min={0} max={10} step={0.05} onChange={changeHandler}
                           name="brightness"
                    />

                    <div slot="footer">
                        <div className="lgs--popup-in-drawer-footer">
                            <WaTooltip for="lgs--reset-layer-settings-to-factory">{'Reset to factory'}</WaTooltip>
                            <WaButton id="lgs--reset-layer-settings-to-factory"
                                      size="s" onClick={resetToFactory}
                                      appearance="outlined"
                                      variant="brand"
                            >
                                <WaIcon slot="start" size="s" name="arrow-rotate-left"/> {'Reset'}
                            </WaButton>

                            <div className="buttons-bar">
                                <WaTooltip for="lgs--undo-layer-settings-last-changes">{'Undo Last Changes'}</WaTooltip>
                                <WaButton id="lgs--undo-layer-settings-last-changes"
                                          size="s"
                                          appearance="plain"
                                          disabled={!editor.layer.settingsChanged} onClick={undoChanges}>
                                    <WaIcon slot="start" size="s" name="arrow-u-turn-up-left"
                                            variant="regular"/> {'Undo'}
                                </WaButton>

                                <WaTooltip for="lgs--close-layer-settings">{'Close settings'}</WaTooltip>
                                <WaButton id="lgs--close-layer-settings"
                                          size="s"
                                          variant="brand"
                                          onClick={close}>
                                    <WaIcon slot="start" size="s" name="xmark" variant="regular"/> {'Close'}
                                </WaButton>
                            </div>
                        </div>
                    </div>
                </WaCard>
            }
        </>
    )

}
